import { store } from "@src/redux/storeConfig/store";
import TranslationService from "@src/services/TranslationService";
import {
  updateAppTranslation,
  updateThemeSettings,
} from "@store/actions/layout";
import { format, isValid, parseISO } from "date-fns";
// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0;

// ** Returns K format from a number
export const kFormatter = (num) =>
  num > 999 ? `${(num / 1000).toFixed(1)}k` : num;

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, "");

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date();
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  );
};
// DISPLAY LIMITED NUMBER OF ELEMENTS FROM AN ARRAY
export const displayLimitedElementsFromArray = (arr, limit, arrayKey) => {
  if (arrayKey) {
    arr = arr.map((item) => item?.[arrayKey]);
  }
  if (arr.length <= 2) {
    return arr.join(", ");
  } else {
    return arr.slice(0, limit).join(", ") + ",...";
  }
};
/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value,
  formatting = { month: "short", day: "numeric", year: "numeric" }
) => {
  if (!value) return value;
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value);
  let formatting = { month: "short", day: "numeric" };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: "numeric", minute: "numeric" };
  }

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

// Format ISO string to local date-time string
export const formatDateTime = (iso) => {
  try {
    const d = iso instanceof Date ? iso : parseISO(String(iso));
    if (!isValid(d)) return iso;
    // Use non-breaking space before AM/PM to prevent wrapping
    return format(d, "MMM d, yyyy h:mm a").replace(/ (AM|PM)$/, "\u00A0$1");
  } catch {
    return iso;
  }
};

// Sort history activities by created_at (latest first)
export const sortHistoryActivitiesByDate = (activities = []) => {
  return [...(activities || [])].sort(
    (a, b) => new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime()
  );
};

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => {
  //  const dispatch = useDispatch()
  let token = localStorage.getItem("userToken");
  let id = localStorage.getItem("orgId");

  if (token && id) {
    return true;
  } else {
    return false;
  }
};

export const userNotification = () =>
  JSON.parse(localStorage.getItem("notification"));

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  if (userRole === "admin") return "/";
  if (userRole === "client") return "/access-control";
  return "/login";
};

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#7367f01a", // for option hover bg-color
    primary: "#7367f0", // for selected option bg-color
    neutral10: "#7367f0", // for tags bg-color
    neutral20: "#ededed", // for input border-color
    neutral30: "#ededed", // for input hover border-color
  },
});

export const validateMOHEmail = (email) => {
  const emailRegex = /^[^\s@]+@moh\.[^\s@]+$/; // Only allow @moh domain
  return emailRegex.test(email);
};

export function checkImage(url) {
  var image = new Image();
  image.onload = function () {
    if (this.width > 0) {
      return true;
    }
  };
  image.onerror = function () {
    return false;
  };
  return (image.src = url);
}
export function checkImageAsync(url) {
  return new Promise((resolve, reject) => {
    var image = new Image();

    image.onload = function () {
      if (this.width > 0) {
        resolve(true); // Image is valid
      } else {
        resolve(false); // Image exists but has no width
      }
    };

    image.onerror = function () {
      resolve(false); // Image failed to load
    };

    image.src = url;
  });
}

/**
 * GET UNIQUE ID
 * @param {String} prefix
 */
export const getUniqueId = (prefix) => {
  const randomNumber = Math.floor(Math.random() * 1000000000);
  return prefix ? prefix + "-" + randomNumber : randomNumber;
};

export const applyThemeSettings = (settings) => {
  const {
    app_name,
    primary_color,
    secondary_color,
    favicon,
    default_logo,
    white_logo,
    updated_at,
  } = settings;

  // UPDATING COLORS
  document.documentElement.style.setProperty("--primary", `#${primary_color}`);

  document.documentElement.style.setProperty(
    "--secondary",
    `#${secondary_color}`
  );
  // UPDATING FAVICON
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.getElementsByTagName("head")[0].appendChild(link);
  }
  link.href = favicon;
  store.dispatch(
    updateThemeSettings({
      app_name,
      primary_color,
      secondary_color,
      favicon,
      default_logo,
      white_logo,
      updated_at,
    })
  );
};

export const loadAppTranslations = async () => {
  try {
    const response = await TranslationService.fetchTranslations();
    store.dispatch(updateAppTranslation(response));
  } catch (error) {
    console.log(error);
  }
};
export const toCamelCase = (str) => {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "") // optional: remove non-alphanumerics
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, "");
};

export const getInitials = (str) => {
  if (!str) return "";

  const words = str.trim().split(/\s+/);

  if (words.length === 1) {
    // If it's a single word, return first two characters
    return words[0].slice(0, 1).toUpperCase();
  }

  // If more than one word, return the first character of the first two words
  return (words[0][0] + words[1][0]).toUpperCase();
};

export const getSynonym = (props) => {

  const regexBreakingChars = /[\\^$.|?*+()[\]{}]/;

  const dynamicLabels =
    props?.dynamicLabels || store.getState()?.layout?.dynamicLabels;

  const id = props?.id;
  const _id = props?.id?.replace(" ","_");
  let result = id;

  const spllittedIds = id.split(" ");
  let concatenatedResult = ""; 

  if(dynamicLabels?.[_id]){
    
     result = dynamicLabels[_id].value;
  }
  else{
    spllittedIds.forEach((singleId) => {
    // skip matching if token contains regex special characters
    if (regexBreakingChars.test(singleId)) {
      concatenatedResult += singleId + " ";
      return;
    }

    const match = Object.entries(dynamicLabels || {}).find(([key]) =>
      new RegExp(`^${singleId}$`, "i").test(key)
    );

    if (match) {
      concatenatedResult += match[1].value + " ";
    } else {
      concatenatedResult += singleId + " ";
    }

  });
  result = concatenatedResult.trim();
  }
  return result;
};
